import { Alert, Button, Stack } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  CallOutcomeModals,
  ExploreWorkspace,
  JobPickerModal,
  PassConfirmModal,
  EXPLORE_JOB_PICKER_PAGE_SIZE,
  EXPLORE_QUEUE_LIMIT,
  exploreQueueQueryKey,
  useExploreQueueWs,
} from "../components/explore";
import { EmptyState, LoadingOverlay, PageHeader } from "../components/ui";
import { interviewPrepQueryKey } from "../constants/recruiterAi";
import { useAuth } from "../context/useAuth";
import { isIdentityVerified } from "../lib/accountVerification";
import { candidatesApi, jobsApi, queueApi, recruiterApi, type QueueMatch } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import { NAV_COPY, NAV_ICONS } from "../lib/navCopy";
import { formatCities } from "../lib/format";
import {
  exploreCallingUnlocked,
  exploreLockCopy,
  recruiterHasExploreSeat,
} from "../lib/kyc";
import type { Job } from "../types/api";

export function ExplorePage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const identityVerified = isIdentityVerified(user);
  const [jobId, setJobId] = useState<string | null>(null);
  const [pickedJob, setPickedJob] = useState<Job | null>(null);
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [tab, setTab] = useState<string | null>("queue");
  const [mustHaveOnly, setMustHaveOnly] = useState(true);
  const [callableOnly, setCallableOnly] = useState(true);
  const [withinBudgetOnly, setWithinBudgetOnly] = useState(false);
  const [matchingLocationsOnly, setMatchingLocationsOnly] = useState(false);
  const [active, setActive] = useState<QueueMatch | null>(null);
  const [dispOpen, setDispOpen] = useState(false);
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [rejectReason, setRejectReason] = useState<string | null>(null);
  const [neverCall, setNeverCall] = useState(false);
  const [callStartedAt, setCallStartedAt] = useState<number | null>(null);
  const [pendingDisposition, setPendingDisposition] = useState<string | null>(null);
  const [passTarget, setPassTarget] = useState<QueueMatch | null>(null);
  const [passReason, setPassReason] = useState<string | null>(null);

  const jobsQuery = useQuery({
    queryKey: ["explore-jobs"],
    queryFn: () => jobsApi.list({ page_size: EXPLORE_JOB_PICKER_PAGE_SIZE }),
  });
  const profileQuery = useQuery({ queryKey: ["my-profile"], queryFn: recruiterApi.myProfile });
  const hasSeat = recruiterHasExploreSeat(profileQuery.data);
  const callingOpen = exploreCallingUnlocked(profileQuery.data, identityVerified);
  const lockCopy = exploreLockCopy(profileQuery.data, identityVerified);

  const queueQuery = useQuery({
    queryKey: exploreQueueQueryKey(jobId, mustHaveOnly, callableOnly, withinBudgetOnly, matchingLocationsOnly),
    queryFn: () =>
      queueApi.list(jobId!, EXPLORE_QUEUE_LIMIT, mustHaveOnly, withinBudgetOnly, matchingLocationsOnly, callableOnly),
    enabled: callingOpen && !!jobId && tab === "queue",
  });

  const cooldownQuery = useQuery({
    queryKey: ["explore-cooldown", jobId],
    queryFn: () => queueApi.cooldown(jobId!),
    enabled: callingOpen && !!jobId && tab === "cooldown",
  });

  const savedQuery = useQuery({
    queryKey: ["explore-saved", jobId],
    queryFn: () => queueApi.saved(jobId!),
    enabled: callingOpen && !!jobId,
  });

  const ownedQuery = useQuery({
    queryKey: ["explore-owned"],
    queryFn: () => queueApi.myOwned(),
    enabled: callingOpen && tab === "owned",
  });

  const briefingQuery = useQuery({
    queryKey: interviewPrepQueryKey(active?.candidate_id ?? "", active?.job_id ?? ""),
    queryFn: () => candidatesApi.interviewPrep(active!.candidate_id, { job_id: active!.job_id }),
    enabled: dispOpen && !!active?.candidate_id && !!active?.job_id,
    retry: false,
  });

  const selectedJob = useMemo(
    () => pickedJob ?? jobsQuery.data?.items.find((j) => j.id === jobId),
    [pickedJob, jobsQuery.data, jobId],
  );

  const removeMatchLocally = useCallback(
    (matchId: string) => {
      queryClient.setQueryData<QueueMatch[]>(
        exploreQueueQueryKey(jobId, mustHaveOnly, callableOnly, withinBudgetOnly, matchingLocationsOnly),
        (old) => (old ?? []).filter((m) => m.id !== matchId),
      );
    },
    [jobId, mustHaveOnly, callableOnly, withinBudgetOnly, matchingLocationsOnly, queryClient],
  );

  useExploreQueueWs({
    enabled: callingOpen,
    jobId,
    removeMatchLocally,
  });

  const claimMutation = useMutation({
    mutationFn: (m: QueueMatch) => queueApi.claim(m.id),
    onSuccess: (claimed) => {
      setActive(claimed);
      removeMatchLocally(claimed.id);
      if (claimed.candidate?.phone) {
        setCallStartedAt(Date.now());
      }
      setDispOpen(true);
      void queryClient.invalidateQueries({ queryKey: ["explore-queue", jobId] });
      void queryClient.invalidateQueries({ queryKey: ["explore-owned"] });
      void queryClient.invalidateQueries({ queryKey: ["explore-saved", jobId] });
    },
    onError: (err: unknown, match) => {
      if (err instanceof ApiError && err.status === 409) {
        notifications.show({
          color: "orange",
          title: "Just claimed",
          message: "Another recruiter got there first — skipping to next.",
        });
        removeMatchLocally(match.id);
        void queryClient.invalidateQueries({ queryKey: ["explore-queue", jobId] });
        void queryClient.invalidateQueries({ queryKey: ["explore-saved", jobId] });
        return;
      }
      notifications.show({
        color: "red",
        title: "Could not claim",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const resetCallSession = () => {
    setDispOpen(false);
    setReleaseConfirmOpen(false);
    setActive(null);
    setNotes("");
    setRejectReason(null);
    setNeverCall(false);
    setCallStartedAt(null);
    setPendingDisposition(null);
  };

  const dispositionMutation = useMutation({
    mutationFn: (payload: { disposition: string }) => {
      if (!active) throw new Error("No active match");
      const duration =
        callStartedAt != null ? Math.max(0, Math.round((Date.now() - callStartedAt) / 1000)) : undefined;
      return queueApi.disposition(active.id, {
        disposition: payload.disposition,
        notes: notes || undefined,
        duration_seconds: duration,
        reject_reason: payload.disposition === "not_a_fit" ? rejectReason ?? undefined : undefined,
        never_call_again: payload.disposition === "not_interested" ? neverCall : false,
      }, crypto.randomUUID());
    },
    onSuccess: (result) => {
      const submissionId = result.submission_id;
      notifications.show({
        color: "teal",
        message: submissionId ? "Submitted — opening the submission" : "Disposition saved",
      });
      resetCallSession();
      void queryClient.invalidateQueries({ queryKey: ["explore-queue", jobId] });
      void queryClient.invalidateQueries({ queryKey: ["explore-owned"] });
      void queryClient.invalidateQueries({ queryKey: ["explore-cooldown", jobId] });
      void queryClient.invalidateQueries({ queryKey: ["explore-saved", jobId] });
      if (submissionId) {
        navigate(`/submissions/${submissionId}`);
      }
    },
    onError: (err: unknown) => {
      notifications.show({
        color: "red",
        title: err instanceof ApiError && err.code === "no_stages" ? "This job has no hiring stages" : "Disposition failed",
        message: err instanceof Error ? err.message : "Unknown error",
      });
      setPendingDisposition(null);
    },
  });

  const releaseMutation = useMutation({
    mutationFn: () => queueApi.release(active!.id),
    onSuccess: () => {
      resetCallSession();
      void queryClient.invalidateQueries({ queryKey: ["explore-queue", jobId] });
      void queryClient.invalidateQueries({ queryKey: ["explore-saved", jobId] });
    },
    onError: (err: unknown) => {
      notifications.show({
        color: "red",
        title: "Could not release",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  const saveForwardMutation = useMutation({
    mutationFn: (m: QueueMatch) => queueApi.saveForward(m.id),
    onSuccess: (bank, match) => {
      queryClient.setQueryData(["explore-saved", jobId], bank);
      removeMatchLocally(match.id);
      notifications.show({ color: "teal", message: "Saved — showing the next person." });
      void queryClient.invalidateQueries({ queryKey: ["explore-queue", jobId] });
      void queryClient.invalidateQueries({ queryKey: ["explore-saved", jobId] });
    },
    onError: (err: unknown) => {
      notifications.show({
        color: err instanceof ApiError && err.code === "save_forward_full" ? "orange" : "red",
        title: err instanceof ApiError && err.code === "save_forward_full" ? "Bank full" : "Could not save",
        message: err instanceof Error ? err.message : "Unknown error",
      });
      void queryClient.invalidateQueries({ queryKey: ["explore-saved", jobId] });
    },
  });

  const passMutation = useMutation({
    mutationFn: ({ match, reason }: { match: QueueMatch; reason: string }) => queueApi.pass(match.id, reason),
    onSuccess: (bank, { match }) => {
      queryClient.setQueryData(["explore-saved", jobId], bank);
      removeMatchLocally(match.id);
      setPassTarget(null);
      setPassReason(null);
      notifications.show({ color: "teal", message: "Passed — showing the next person." });
      void queryClient.invalidateQueries({ queryKey: ["explore-queue", jobId] });
      void queryClient.invalidateQueries({ queryKey: ["explore-saved", jobId] });
    },
    onError: (err: unknown) => {
      notifications.show({
        color: err instanceof ApiError && err.code === "pass_limit_reached" ? "orange" : "red",
        title: err instanceof ApiError && err.code === "pass_limit_reached" ? "Daily pass limit" : "Could not pass",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  if (profileQuery.isLoading) {
    return <LoadingOverlay />;
  }
  if (profileQuery.isFetched && !hasSeat) {
    return <Navigate to="/" replace />;
  }

  return (
    <Stack gap="var(--axio-unit-32)">
      <PageHeader
        title={NAV_COPY.recruiter.callQueue}
        icon={NAV_ICONS.callQueue}
        subtitle="Start with the top-ranked person for this job — call, save forward, or pass, then move on."
        actions={
          <Button variant="default" onClick={() => setJobPickerOpen(true)} disabled={!callingOpen}>
            {selectedJob ? `${selectedJob.title} · ${formatCities(selectedJob.cities)}` : "Choose job"}
          </Button>
        }
      />

      {active && !dispOpen && (
        <Alert
          color="orange"
          title="Log this call"
          withCloseButton={false}
        >
          You claimed {active.candidate?.full_name ?? "a candidate"}. Finish the disposition so the lock is released.
          <Button size="xs" mt="sm" onClick={() => setDispOpen(true)}>
            Log outcome
          </Button>
        </Alert>
      )}

      {!callingOpen && (
        <Alert color="yellow" title={lockCopy.alertTitle}>
          {lockCopy.alertBody}
          <Button component={Link} to={lockCopy.ctaTo} size="xs" mt="sm" color="yellow">
            {lockCopy.ctaLabel}
          </Button>
        </Alert>
      )}

      {!callingOpen ? (
        <EmptyState
          icon={NAV_ICONS.callQueue}
          title={lockCopy.emptyTitle}
          description={lockCopy.emptyDescription}
          action={
            <Button component={Link} to={lockCopy.ctaTo} color="violet">
              {lockCopy.ctaLabel}
            </Button>
          }
        />
      ) : !jobId ? (
        <EmptyState
          icon={NAV_ICONS.callQueue}
          title="Pick a job"
          description="Select an open job to load the next person in the calling queue."
        />
      ) : (
        <ExploreWorkspace
          tab={tab}
          onTabChange={setTab}
          mustHaveOnly={mustHaveOnly}
          callableOnly={callableOnly}
          withinBudgetOnly={withinBudgetOnly}
          matchingLocationsOnly={matchingLocationsOnly}
          onMustHaveOnly={setMustHaveOnly}
          onCallableOnly={setCallableOnly}
          onWithinBudgetOnly={setWithinBudgetOnly}
          onMatchingLocationsOnly={setMatchingLocationsOnly}
          queueQuery={queueQuery}
          cooldownQuery={cooldownQuery}
          ownedQuery={ownedQuery}
          savedQuery={savedQuery}
          selectedJob={selectedJob}
          callingOpen={callingOpen}
          claimingId={claimMutation.isPending ? claimMutation.variables?.id : undefined}
          savingId={saveForwardMutation.isPending ? saveForwardMutation.variables?.id : undefined}
          passingId={passMutation.isPending ? passMutation.variables?.match.id : undefined}
          onCall={(card) => claimMutation.mutate(card)}
          onSaveForward={(card) => saveForwardMutation.mutate(card)}
          onPass={(card) => {
            setPassTarget(card);
            setPassReason(null);
          }}
          onResumeCall={(m) => {
            setActive(m);
            setDispOpen(true);
          }}
        />
      )}

      <JobPickerModal
        opened={jobPickerOpen}
        onClose={() => setJobPickerOpen(false)}
        onPick={(job) => {
          setPickedJob(job);
          setJobId(job.id);
        }}
      />

      <CallOutcomeModals
        dispOpen={dispOpen}
        releaseConfirmOpen={releaseConfirmOpen}
        active={active}
        job={selectedJob}
        notes={notes}
        rejectReason={rejectReason}
        neverCall={neverCall}
        pendingDisposition={pendingDisposition}
        briefingLoading={briefingQuery.isLoading && !briefingQuery.data}
        briefingError={briefingQuery.isError && !briefingQuery.data}
        briefingSummary={briefingQuery.data?.summary}
        onRetryBriefing={() => void briefingQuery.refetch()}
        onNotesChange={setNotes}
        onRejectReasonChange={setRejectReason}
        onNeverCallChange={setNeverCall}
        onPendingDisposition={setPendingDisposition}
        onDisposition={(disposition) => dispositionMutation.mutate({ disposition })}
        onRequestRelease={() => setReleaseConfirmOpen(true)}
        onCancelRelease={() => setReleaseConfirmOpen(false)}
        onConfirmRelease={() => releaseMutation.mutate()}
        dispositionPending={dispositionMutation.isPending}
        releasePending={releaseMutation.isPending}
      />

      <PassConfirmModal
        opened={passTarget != null}
        candidateName={passTarget?.candidate?.full_name}
        rejectReason={passReason}
        pending={passMutation.isPending}
        onRejectReasonChange={setPassReason}
        onCancel={() => {
          setPassTarget(null);
          setPassReason(null);
        }}
        onConfirm={() => {
          if (passTarget && passReason) {
            passMutation.mutate({ match: passTarget, reason: passReason });
          }
        }}
      />
    </Stack>
  );
}
